<!-- eslint-disable vue/no-mutating-props -->
<template>
  <v-dialog
    persistent
    :fullscreen="fullScreen"
    hide-overlay
    scrollable
    v-model="view"
    max-width="800"
    max-height="80hv"
  >
    <v-card style="display: flex; flex-direction: column">
      <v-card-title class="primary pa-2 pl-4 text-subtitle-1">
        <span class="white--text">{{ fileItems.UploadName }}</span>
        <v-spacer></v-spacer>
        <v-icon dark @click="$emit('close')">mdi-close</v-icon>
      </v-card-title>

      <v-card-text class="pa-2 pt-0" style="background-color: grey">
        <div
          v-for="file in fileItems.FileSegments"
          :key="file.PhysicalFileId || file.FileName"
        >
          <div class="filename pt-2 white--text">{{ file.FileName }}</div>

          <!-- Pdf Preview -->
          <div v-if="file.FileExtension === 'pdf'">
            <pdf-preview :fileItem="file" />
          </div>

          <!-- HEIC preview (convert in browser) -->
          <div
            v-else-if="file.FileExtension === 'heic'"
            class="file-preview-container"
          >
            <div v-if="!getPreviewUrl(file)">
              <v-layout row justify-center align-center class="mt-4">
                <v-progress-circular indeterminate></v-progress-circular>
                <span class="white--text ml-3">Generating preview…</span>
              </v-layout>
            </div>
            <v-img v-else :src="getPreviewUrl(file)" contain max-height="700" />
          </div>

          <!-- Image preview -->
          <div
            v-else-if="
              file.FileExtension === 'png' ||
              file.FileExtension === 'jpg' ||
              file.FileExtension === 'jpeg' ||
              file.FileExtension === 'webp'
            "
            class="file-preview-container"
          >
            <v-img
              :src="'data:image/jpg;base64,' + file.Base64"
              contain
              max-height="700"
            />
          </div>

          <!-- text preview -->
          <div
            v-else-if="file.FileExtension === 'txt'"
            class="file-preview-container"
            style="font-size: 1em; overflow-y: auto"
          >
            {{ formatBase64ToText(file.Base64) }}
          </div>

          <!-- no preview -->
          <v-card-text v-else class="file-preview-container">
            <h5 class="white--text">
              {{ $t("components.filepreview.no_preview") }}
            </h5>
          </v-card-text>
        </div>
      </v-card-text>

      <v-card-actions v-if="!fullScreen" class="justify-end">
        <v-btn text @click="downloadFile(fileItems)"> Download File </v-btn>
        <v-btn text @click="$emit('close')">
          {{ $t("globals.close") }}
        </v-btn>
      </v-card-actions>

      <v-dialog scrollable v-model="loadingFile" persistent max-width="200">
        <v-card>
          <v-card-title>{{
            $t("components.filepreview.please_wait")
          }}</v-card-title>
          <v-card-text>
            <v-container fluid grid-list-md>
              <v-layout row justify-center>
                <v-flex xs12 sm6 md4 lg3>
                  <v-progress-circular indeterminate></v-progress-circular>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import PdfPreview from "@/components/controls/PdfPreview.vue";
import { saveAs } from "file-saver";
import heic2any from "heic2any";

export default {
  name: "file-preview-base64",
  props: ["fileItems", "view", "fullScreen"],
  components: {
    PdfPreview,
  },
  mounted() {
    this.previewZoom = this.fullScreen ? 60 : 100;
    // Precompute HEIC previews (non-blocking)
    this.prepareHeicPreviews();
  },
  watch: {
    fileItems: {
      deep: true,
      handler() {
        this.prepareHeicPreviews();
      },
    },
  },
  data() {
    return {
      loadingFile: false,
      previewZoom: 60,
      file: null,
      pdfSrc: null,
      pageCount: 0,
      src: null,
      // Store object URLs for previews without mutating props
      previewMap: {}, // { [id]: objectUrl or dataUrl }
      preparingSet: new Set(),
    };
  },
  beforeDestroy() {
    // Revoke all object URLs we created
    Object.values(this.previewMap).forEach((url) => {
      if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
    });
  },
  methods: {
    // ---------- DOWNLOAD ----------
    async downloadFile(item) {
      try {
        this.loadingFile = true;
        const seg = item.FileSegments[0];
        const mime = this.getMimeByExtension(seg.FileExtension);
        const blob = this.b64toBlob(seg.Base64, mime);
        saveAs(blob, seg.FileName || item.UploadName || "file");
      } catch (error) {
        this.error = error;
      } finally {
        this.loadingFile = false;
      }
    },

    // ---------- PREVIEW HELPERS ----------
    getId(file) {
      return file.PhysicalFileId || file.FileName || Math.random().toString(36);
    },
    getPreviewUrl(file) {
      const id = this.getId(file);
      // If not yet prepared and it's HEIC, kick off preparation
      if (
        file.FileExtension === "heic" &&
        !this.previewMap[id] &&
        !this.preparingSet.has(id)
      ) {
        this.prepareHeicPreview(file);
      }
      return this.previewMap[id] || null;
    },
    async prepareHeicPreviews() {
      if (!this.fileItems || !this.fileItems.FileSegments) return;
      for (const file of this.fileItems.FileSegments) {
        if (file.FileExtension === "heic") {
          this.prepareHeicPreview(file);
        }
      }
    },
    async prepareHeicPreview(file) {
      const id = this.getId(file);
      if (this.previewMap[id] || this.preparingSet.has(id)) return;
      this.preparingSet.add(id);
      try {
        const dataUrl = this.ensureDataUrl(file.Base64, "image/heic");
        const heicBlob = await this.dataUrlToBlob(dataUrl);

        let outBlob;
        try {
          outBlob = await heic2any({
            blob: heicBlob,
            toType: "image/jpeg",
            quality: 0.92,
          });

          // Handle array return + ensure reactivity
          const blobOut = Array.isArray(outBlob) ? outBlob[0] : outBlob;
          if (this.previewMap[id] && this.previewMap[id].startsWith("blob:")) {
            URL.revokeObjectURL(this.previewMap[id]);
          }
          this.$set(this.previewMap, id, URL.createObjectURL(blobOut));
        } catch (convErr) {
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent
          );
          if (isSafari) {
            this.$set(this.previewMap, id, dataUrl); // native HEIC render in Safari
          } else {
            console.error("HEIC conversion failed:", convErr);
          }
        }
      } catch (e) {
        console.error("HEIC preview error:", e);
      } finally {
        this.preparingSet.delete(id);
      }
    },

    // ---------- UTILITIES ----------
    b64toBlob(b64Data, contentType = "", sliceSize = 512) {
      const byteCharacters = atob(b64Data);
      const byteArrays = [];

      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      return new Blob(byteArrays, { type: contentType });
    },
    formatBase64ToText(base64Txt) {
      return atob(base64Txt);
    },
    getMimeByExtension(ext) {
      const e = (ext || "").toLowerCase();
      switch (e) {
        case "pdf":
          return "application/pdf";
        case "png":
          return "image/png";
        case "jpg":
        case "jpeg":
          return "image/jpeg";
        case "heic":
          return "image/heic";
        case "webp":
          return "image/webp";
        case "txt":
          return "text/plain";
        default:
          return "application/octet-stream";
      }
    },
    ensureDataUrl(b64, mime) {
      return b64.startsWith("data:") ? b64 : `data:${mime};base64,${b64}`;
    },
    async dataUrlToBlob(dataUrl) {
      const res = await fetch(dataUrl);
      return await res.blob();
    },
  },
};
</script>

<style>
.file-preview-container {
  height: 100%;
  width: 750px;
  margin: 10px auto;
}
.filename {
  font-size: 14px;
  font-weight: 600;
}
</style>
